import { createAuthenticatedApi, createPublicApi, extractApiErrorMessage } from '@/lib/api'
import type { RolePermissions } from '@/types/users'

export interface CompanySettingsPayload {
  companyName: string
  fullAddress: string
  city: string
  country: string
  contactPhone: string
  mobile: string
  email: string
  logo: string
  twitter: string
  facebook: string
  instagram: string
}

export interface AdminSettingsPayload {
  adminUsername: string
  adminEmail: string
}

interface SettingsResponse {
  success: boolean
  data: {
    company: CompanySettingsPayload
    admin: AdminSettingsPayload
    rolePermissions: RolePermissions
  }
}

interface PublicSettingsResponse {
  success: boolean
  data: {
    companyName: string
    logo: string
  }
}

const api = createAuthenticatedApi()
const publicApi = createPublicApi()

export const getSettings = async () => {
  try {
    const response = await api.get<SettingsResponse>('/settings')
    return response.data.data
  } catch (error) {
    throw new Error(extractApiErrorMessage(error, 'Failed to load settings'))
  }
}

export const getPublicBranding = async () => {
  try {
    const response = await publicApi.get<PublicSettingsResponse>('/settings/public')
    return response.data.data
  } catch (error) {
    throw new Error(extractApiErrorMessage(error, 'Failed to load company branding'))
  }
}

export const updateCompanySettings = async (payload: CompanySettingsPayload) => {
  try {
    const response = await api.put('/settings/company', payload)
    return response.data.data as CompanySettingsPayload
  } catch (error) {
    throw new Error(extractApiErrorMessage(error, 'Failed to save company settings'))
  }
}

export const updateAdminSettings = async (payload: { adminEmail: string; adminPassword?: string }) => {
  try {
    const response = await api.put('/settings/admin', payload)
    return response.data.data as AdminSettingsPayload
  } catch (error) {
    throw new Error(extractApiErrorMessage(error, 'Failed to save admin settings'))
  }
}

export const updateRolePermissions = async (rolePermissions: RolePermissions) => {
  try {
    const response = await api.put('/settings/role-permissions', { rolePermissions })
    return response.data.data.rolePermissions as RolePermissions
  } catch (error) {
    throw new Error(extractApiErrorMessage(error, 'Failed to save role permissions'))
  }
}
